"use client";

import { CommandMenu } from "@/app/(v2)/_components/CommandMenu";
import MainSidebar from "@/app/(v2)/_components/navigation/MainSidebar";
import MainSidebarSkeleton from "@/app/(v2)/_components/navigation/MainSidebarSkeleton";
import { APP_SIDEBAR_WIDTH } from "@/app/(v2)/_components/navigation/sidebar.constants";
import { SideRail } from "@/components/layout/SideRail";
import { SupportModal } from "@/components/support/SupportModal";
import { B3OSVoiceProvider } from "@/components/voice/realtime/B3OSVoiceControl";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import { useCommandMenuStore } from "@/stores/commandMenuStore";
import { usePathname } from "next/navigation";
import { Suspense, useEffect } from "react";

/**
 * Wraps public-page children with the v2-style sidebar for authenticated users.
 * Unauthenticated users see children without the sidebar.
 *
 * IMPORTANT: The wrapper tree is kept stable across auth state changes — the
 * sidebar is toggled in place via a conditional render, and the children
 * always live inside the same `<main>` element. Previously this component
 * returned `<>{children}</>` when unauthenticated and a nested `<div><main>`
 * tree when authenticated; switching between those shapes unmounts and
 * remounts every child component when auth resolves (roughly 7–12s into
 * page load), wiping per-component state and triggering duplicate fetches.
 */
export function ConditionalPublicLayout({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const pathname = usePathname();
  const useMarketingChrome =
    pathname === "/" || pathname === "/pricing" || pathname === "/agents" || pathname.startsWith("/blog");
  const showSidebar = isAuthenticated && !useMarketingChrome;

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (useMarketingChrome) {
      html.classList.remove("dark", "theme-dark");
      html.setAttribute("data-theme", "light");
      html.style.colorScheme = "light";
      body.classList.remove("chakra-ui-dark");
      body.classList.add("chakra-ui-light");
      return;
    }

    const saved = localStorage.getItem("chakra-ui-color-mode");
    const useDark = saved === "dark";
    html.classList.toggle("dark", useDark);
    html.classList.toggle("theme-dark", useDark);
    html.setAttribute("data-theme", useDark ? "dark" : "light");
    html.style.colorScheme = useDark ? "dark" : "light";
    body.classList.toggle("chakra-ui-dark", useDark);
    body.classList.toggle("chakra-ui-light", !useDark);
  }, [useMarketingChrome]);

  const commandMenuOpen = useCommandMenuStore(state => state.isOpen);
  const setCommandMenuOpen = useCommandMenuStore(state => state.setOpen);

  return (
    <div
      className={`bg-ds-surface-secondary min-h-screen w-full dark:bg-zinc-950 ${showSidebar ? "overflow-x-clip" : ""}`}
    >
      {showSidebar && (
        <>
          <CommandMenu open={commandMenuOpen} onOpenChange={setCommandMenuOpen} />
          <Suspense fallback={<MainSidebarSkeleton />}>
            <MainSidebar className="fixed left-0 top-[var(--banner-offset,0px)] z-30 h-[calc(100vh-var(--banner-offset,0px))]" />
          </Suspense>
          <B3OSVoiceProvider>
            <SideRail />
          </B3OSVoiceProvider>
          <SupportModal />
        </>
      )}
      {/* Generic container (not <main>) because the page's layout.tsx
          already renders a <main> inside `children`. */}
      <div
        className={`bg-ds-surface-secondary min-h-screen transition-all duration-300 dark:bg-zinc-950 ${showSidebar ? "overflow-x-clip" : ""}`}
        style={
          showSidebar
            ? { marginLeft: `${APP_SIDEBAR_WIDTH}px`, width: `calc(100vw - ${APP_SIDEBAR_WIDTH}px)` }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}
